import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function StatusPill({ s }) {
  const color = s === "confirmed" || s === "completed" ? "bg-sage-700 text-white"
    : s === "declined" || s === "cancelled" ? "bg-destructive/10 text-destructive"
    : "bg-sand-200 text-sage-800";
  return <span className={`text-xs px-3 py-1 rounded-full uppercase tracking-wider ${color}`}>{s}</span>;
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl text-sage-800 mb-4">{title}</h2>
      <div className="rounded-3xl bg-white border border-sand-200/60 overflow-hidden">{children}</div>
    </div>
  );
}

async function act(endpoint, id, action, refresh) {
  try {
    await api.post(`${endpoint}/${id}/action`, { action });
    toast.success(`Marked ${action}`);
    refresh();
  } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
}

function Consults() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/consultations").then(({data})=>setItems(data));
  useEffect(() => { load(); }, []);
  return (
    <Section title={`Consultation Requests (${items.length})`}>
      <Table>
        <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>Provider</TableHead><TableHead>Type</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map(x=>(
            <TableRow key={x.id} data-testid={`consult-row-${x.id}`}>
              <TableCell><div className="text-sage-800">{x.name}</div><div className="text-xs text-sage-800/60">{x.email}</div></TableCell>
              <TableCell className="capitalize">{x.provider}</TableCell>
              <TableCell>{x.consultation_type}</TableCell>
              <TableCell>{x.date} · {x.time}</TableCell>
              <TableCell><StatusPill s={x.status} /></TableCell>
              <TableCell className="text-right space-x-2">
                {x.status === "requested" && <>
                  <Button size="sm" className="btn-pill h-8 bg-sage-700 hover:bg-sage-800 text-white" data-testid={`consult-approve-${x.id}`} onClick={()=>act("/consultations", x.id, "approve", load)}>Approve</Button>
                  <Button size="sm" variant="outline" className="btn-pill h-8" data-testid={`consult-decline-${x.id}`} onClick={()=>act("/consultations", x.id, "decline", load)}>Decline</Button>
                </>}
                {x.status === "confirmed" && <Button size="sm" variant="outline" className="btn-pill h-8" onClick={()=>act("/consultations", x.id, "complete", load)}>Mark completed</Button>}
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-sage-800/50">No requests yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Section>
  );
}

function IVs() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/iv-appointments").then(({data})=>setItems(data));
  useEffect(() => { load(); }, []);
  return (
    <Section title={`IV Appointments (${items.length})`}>
      <Table>
        <TableHeader><TableRow><TableHead>Member</TableHead><TableHead>IV</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map(x=>(
            <TableRow key={x.id} data-testid={`iv-row-${x.id}`}>
              <TableCell><div>{x.user_name}</div><div className="text-xs text-sage-800/60">{x.user_email}</div></TableCell>
              <TableCell>{x.iv_type}</TableCell>
              <TableCell>{x.date} · {x.time}</TableCell>
              <TableCell><StatusPill s={x.status} /></TableCell>
              <TableCell className="text-right space-x-2">
                {x.status === "requested" && <>
                  <Button size="sm" className="btn-pill h-8 bg-sage-700 hover:bg-sage-800 text-white" data-testid={`iv-approve-${x.id}`} onClick={()=>act("/iv-appointments", x.id, "approve", load)}>Approve</Button>
                  <Button size="sm" variant="outline" className="btn-pill h-8" data-testid={`iv-decline-${x.id}`} onClick={()=>act("/iv-appointments", x.id, "decline", load)}>Decline</Button>
                </>}
                {x.status === "confirmed" && <Button size="sm" variant="outline" className="btn-pill h-8" onClick={()=>act("/iv-appointments", x.id, "complete", load)}>Mark completed</Button>}
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-sage-800/50">No IV appointments yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Section>
  );
}

function MembershipRequests() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/membership-requests").then(({data})=>setItems(data)); }, []);
  return (
    <Section title={`Membership Requests (${items.length})`}>
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Tier</TableHead><TableHead>Email</TableHead><TableHead>Requested</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map(x=>(
            <TableRow key={x.id}>
              <TableCell>{x.name}</TableCell>
              <TableCell className="capitalize">{x.tier}</TableCell>
              <TableCell>{x.email}</TableCell>
              <TableCell>{x.created_at?.slice(0,10)}</TableCell>
            </TableRow>
          ))}
          {items.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-sage-800/50">No requests yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Section>
  );
}

function Members() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/members").then(({data})=>setItems(data)); }, []);
  return (
    <Section title={`Members (${items.length})`}>
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Plan</TableHead><TableHead>Joined</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map(u=>(
            <TableRow key={u.id}>
              <TableCell>{u.name}</TableCell><TableCell>{u.email}</TableCell><TableCell>{u.phone||"—"}</TableCell>
              <TableCell>{u.membership_tier || "—"}</TableCell><TableCell>{u.created_at?.slice(0,10)}</TableCell>
            </TableRow>
          ))}
          {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-sage-800/50">No members yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Section>
  );
}

function Subscribers() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/subscribers").then(({data})=>setItems(data)); }, []);
  const csv = () => {
    const rows = [["Name","Email","Phone","SMS opt-in","Source","Joined"]].concat(
      items.map(s => [s.name||"", s.email, s.phone||"", s.sms_opt_in?"yes":"no", s.source||"", (s.created_at||"").slice(0,10)])
    );
    const blob = new Blob([rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `restore-subscribers-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };
  return (
    <Section title={`Marketing Subscribers (${items.length})`}>
      <div className="p-4 border-b border-sand-200/70 flex justify-between items-center">
        <div className="text-xs text-sage-800/60">Captured via welcome pop-up and other opt-in forms</div>
        <Button onClick={csv} disabled={items.length===0} size="sm" variant="outline" className="btn-pill h-8" data-testid="subs-export">Export CSV</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>SMS</TableHead><TableHead>Source</TableHead><TableHead>Joined</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map(s=>(
            <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
              <TableCell>{s.name||"—"}</TableCell>
              <TableCell>{s.email}</TableCell>
              <TableCell>{s.phone||"—"}</TableCell>
              <TableCell>{s.sms_opt_in ? <span className="text-xs px-2 py-0.5 rounded-full bg-sage-700 text-white">Yes</span> : <span className="text-xs text-sage-800/50">No</span>}</TableCell>
              <TableCell className="text-xs text-sage-800/70">{s.source||"—"}</TableCell>
              <TableCell>{(s.created_at||"").slice(0,10)}</TableCell>
            </TableRow>
          ))}
          {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-sage-800/50">No subscribers yet — the pop-up will start collecting them.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Section>
  );
}

export default function Admin() {
  return (
    <div className="section-y" data-testid="admin-page">
      <div className="container-x">
        <div className="overline">Admin</div>
        <h1 className="font-display text-4xl text-sage-800 mt-3">Restore Wellness Dashboard</h1>
        <Tabs defaultValue="consults" className="mt-8">
          <TabsList className="bg-sand-100 rounded-full p-1 h-auto">
            <TabsTrigger value="consults" className="rounded-full data-[state=active]:bg-sage-700 data-[state=active]:text-white px-5 py-2" data-testid="tab-consults">Consults</TabsTrigger>
            <TabsTrigger value="ivs" className="rounded-full data-[state=active]:bg-sage-700 data-[state=active]:text-white px-5 py-2" data-testid="tab-ivs">IV Appointments</TabsTrigger>
            <TabsTrigger value="mreq" className="rounded-full data-[state=active]:bg-sage-700 data-[state=active]:text-white px-5 py-2" data-testid="tab-mreq">Membership Requests</TabsTrigger>
            <TabsTrigger value="members" className="rounded-full data-[state=active]:bg-sage-700 data-[state=active]:text-white px-5 py-2" data-testid="tab-members">Members</TabsTrigger>
            <TabsTrigger value="subs" className="rounded-full data-[state=active]:bg-sage-700 data-[state=active]:text-white px-5 py-2" data-testid="tab-subs">Subscribers</TabsTrigger>
          </TabsList>
          <TabsContent value="consults" className="mt-8"><Consults /></TabsContent>
          <TabsContent value="ivs" className="mt-8"><IVs /></TabsContent>
          <TabsContent value="mreq" className="mt-8"><MembershipRequests /></TabsContent>
          <TabsContent value="members" className="mt-8"><Members /></TabsContent>
          <TabsContent value="subs" className="mt-8"><Subscribers /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
